import { Link } from '@remix-run/react';
import { ArrowUpRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { CalendarEarning } from '~/types/earningsCalendar.types';

import type { EarningsSearchHit } from '../utils/fuzzyMatch';
import { tickerLogoUrl } from '../utils/tickerLogoUrl';

const ROW_CLASS =
  'group flex w-full min-w-0 items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-emerald-50/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-emerald-600 md:gap-3.5 md:px-4 md:py-3';

function TodayBadge() {
  return (
    <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-emerald-700 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white">
      Today
    </span>
  );
}

function ResultLogo({ item }: { item: CalendarEarning }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = item.symbol.slice(0, 2).toUpperCase();
  const logoSrc = tickerLogoUrl(item);

  useEffect(() => {
    setLogoFailed(false);
  }, [item.symbol, logoSrc]);

  return (
    <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-700 md:size-10">
      {!logoFailed ? (
        <img
          key={`${item.symbol}-${logoSrc}`}
          src={logoSrc}
          alt=""
          className="size-5 object-contain md:size-6"
          loading="lazy"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center text-[9px] font-bold text-slate-300 md:text-[10px]">
          {initials}
        </span>
      )}
    </span>
  );
}

function ResultRow({ hit, isToday }: { hit: EarningsSearchHit; isToday: boolean }) {
  const { earning, day, weekLabel } = hit;
  const meta = `${day.weekdayShort} · ${day.date} · ${weekLabel}`;
  const title = `${earning.name} (${earning.symbol}) — ${meta}`;

  const content = (
    <>
      <ResultLogo item={earning} />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold tabular-nums tracking-tight text-[#1c1c1c] md:text-[15px]">
            {earning.symbol}
          </span>
          {isToday ? <TodayBadge /> : null}
        </span>
        <span className="mt-1.5 block text-xs font-medium tabular-nums tracking-tight text-slate-400 md:text-[13px]">
          {meta}
        </span>
      </span>
      <span className="shrink-0 text-xs font-semibold tabular-nums tracking-tight text-slate-600 md:text-[13px]">
        {earning.formattedMarketCap ?? '—'}
      </span>
      {earning.tickerHref ? (
        <ArrowUpRight
          className="h-3.5 w-3.5 shrink-0 text-emerald-700 opacity-70 transition-opacity group-hover:opacity-100"
          strokeWidth={2.5}
          aria-hidden
        />
      ) : (
        <span className="h-3.5 w-3.5 shrink-0" aria-hidden />
      )}
    </>
  );

  if (!earning.tickerHref) {
    return (
      <span className={ROW_CLASS} title={title}>
        {content}
      </span>
    );
  }

  return (
    <Link to={earning.tickerHref} className={ROW_CLASS} title={title}>
      {content}
    </Link>
  );
}

export default function EarningsSearchResults({
  query,
  hits,
  todayWeekIndex,
  todayRowIndex,
}: {
  query: string;
  hits: EarningsSearchHit[];
  todayWeekIndex: number | null;
  todayRowIndex: number | null;
}) {
  if (hits.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-900/[0.08] bg-white px-5 py-10 text-center shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
        <p className="text-sm font-medium text-slate-700">No earnings match “{query.trim()}”</p>
        <p className="mt-1 text-xs text-slate-500">
          Try another ticker or company name across last, this, and next week.
        </p>
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-xl border border-slate-900/[0.06] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.03),0_12px_32px_-20px_rgba(15,23,42,0.12)]"
      role="region"
      aria-label={`Search results for ${query.trim()}`}
    >
      <div className="flex items-center justify-between border-b border-slate-900/[0.04] px-4 py-2.5 md:px-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
          {hits.length} {hits.length === 1 ? 'result' : 'results'}
        </p>
      </div>
      <ul className="divide-y divide-[#e7e0d2]" role="list">
        {hits.map((hit, index) => {
          const isToday =
            todayWeekIndex != null &&
            todayRowIndex != null &&
            hit.weekIndex === todayWeekIndex &&
            hit.dayIndex === todayRowIndex;

          return (
            <li key={`${hit.weekIndex}-${hit.day.date}-${hit.earning.symbol}-${index}`}>
              <ResultRow hit={hit} isToday={isToday} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
